import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Skeleton } from "@/components/ui/Skeleton";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import useNavigationGuard from "@/hooks/useNavigationGuard";

export interface LookupFieldProps {
  label?: string;
  value?: string;
  description?: string;
  placeholder?: string;
  icon?: keyof typeof MaterialCommunityIcons.glyphMap;
  buttonText?: string;
  editing?: boolean;
  loading?: boolean;
  onPress?: () => void;
  params?: { panel: string; id: string };
  showDescription?: boolean; // Hint for skeleton to show description line
  readonly?: boolean; // Never show edit button, even when editing=true
  badge?: React.ReactNode; // Optional badge to show next to value (e.g., "From Project")
}

export function LookupField({
  label,
  value,
  description,
  placeholder = "Not selected",
  icon = "magnify" as keyof typeof MaterialCommunityIcons.glyphMap,
  buttonText = "Select",
  editing = false,
  loading = false,
  onPress,
  params,
  showDescription = false,
  readonly = false,
  badge,
}: LookupFieldProps) {
  const { colors } = useTheme();
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleNavigation = () => {
    if (!params || editing) return;

    // Save current entity to history before navigating
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;

    if (currentPanel && currentId) {
      pushNavigation({
        panel: currentPanel,
        id: currentId,
        title: currentEntity?.currentTitle || value || "",
      });
    }

    // Navigate to the entity
    navigateToEntity(params.panel, params.id);
  };

  // Show skeleton when loading
  if (loading) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View
          style={[
            styles.valueContainer,
            {
              borderColor: colors.border,
              backgroundColor: colors.backgroundSecondary,
            },
          ]}
        >
          <Skeleton width="60%" height={14} />
          {(showDescription || description !== undefined) && (
            <Skeleton width="40%" height={10} style={{ marginTop: 6 }} />
          )}
        </View>
      </View>
    );
  }

  const hasValue = Boolean(value);
  const isClickable = params && !editing && hasValue;

  return (
    <View style={styles.container}>
      {label && <ThemedText style={styles.label}>{label}</ThemedText>}
      <View style={styles.row}>
        <Pressable
          onPress={isClickable ? handleNavigation : undefined}
          style={[
            styles.valueContainer,
            {
              borderColor: colors.border,
              backgroundColor: colors.backgroundSecondary,
            },
          ]}
          disabled={!isClickable}
        >
          {hasValue ? (
            <View>
              <View style={styles.valueRow}>
                <ThemedText
                  style={[styles.value, isClickable && { color: colors.primary }]}
                  type={isClickable ? "link" : "default"}
                >
                  {value}
                </ThemedText>
                {badge}
              </View>
              {description && <ThemedText style={styles.description}>{description}</ThemedText>}
            </View>
          ) : (
            <ThemedText style={styles.placeholder}>{placeholder}</ThemedText>
          )}
        </Pressable>
        {editing && !readonly && onPress && (
          <Button
            text={buttonText}
            icon={icon}
            iconPosition="right"
            iconColor="white"
            textStyle={{ color: "white" }}
            style={[styles.button, { backgroundColor: colors.primary }]}
            onPress={onPress}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  label: {
    fontSize: 14,
    marginBottom: 4,
    fontWeight: "600",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  valueRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  valueContainer: {
    flex: 1,
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    minHeight: 40,
    justifyContent: "center",
  },
  value: {
    fontSize: 14,
  },
  description: {
    fontSize: 11,
    opacity: 0.6,
    marginTop: 2,
  },
  placeholder: {
    opacity: 0.5,
    lineHeight: 24,
  },
  button: {
    height: 40,
    gap: 8,
    paddingHorizontal: 12,
  },
});
